import React from 'react';
import { Search, FileText, Settings, ChevronRight } from 'lucide-react';
import './WorkyMainDemoOpsClientInvoicesScreen.css';

// ── Types ────────────────────────────────────────────────────────────────────

interface InvoiceRow {
    readonly id: string;
    readonly number: string;
    readonly period: string;
    readonly stubs: number;
    readonly amount: string;
    readonly isLatest: boolean;
}

interface ClientCard {
    readonly id: string;
    readonly name: string;
    readonly invoiceCount: number;
    readonly totalBilled: string;
    readonly latestPeriod: string;
}

// ── Static data ──────────────────────────────────────────────────────────────

const KINGSTON_INVOICES: readonly InvoiceRow[] = [
    { id: 'k1', number: 'INV-0044', period: 'Apr 1 – Apr 15, 2026',  stubs: 14, amount: '$6,667.00',  isLatest: true },
    { id: 'k2', number: 'INV-0031', period: 'Mar 16 – Mar 31, 2026', stubs: 22, amount: '$15,840.00', isLatest: false },
    { id: 'k3', number: 'INV-0028', period: 'Mar 1 – Mar 15, 2026',  stubs: 20, amount: '$14,200.00', isLatest: false },
    { id: 'k4', number: 'INV-0021', period: 'Feb 16 – Feb 28, 2026', stubs: 18, amount: '$12,690.00', isLatest: false },
];

const OTHER_CLIENTS: readonly ClientCard[] = [
    { id: 'sunnybrook',   name: 'Sunnybrook Health Sciences Centre', invoiceCount: 12, totalBilled: '$84,320.00', latestPeriod: 'Apr 1 – Apr 15' },
    { id: 'tgh',          name: 'Toronto General Hospital (UHN)',     invoiceCount: 9,  totalBilled: '$61,450.00', latestPeriod: 'Apr 1 – Apr 15' },
    { id: 'ottawa',       name: 'Ottawa Hospital – Civic Campus',     invoiceCount: 7,  totalBilled: '$49,880.00', latestPeriod: 'Apr 1 – Apr 15' },
    { id: 'hamilton',     name: 'Hamilton Health Sciences',           invoiceCount: 11, totalBilled: '$77,600.00', latestPeriod: 'Apr 1 – Apr 15' },
    { id: 'osler',        name: 'William Osler Health System',        invoiceCount: 6,  totalBilled: '$42,100.00', latestPeriod: 'Mar 16 – Mar 31' },
    { id: 'trillium',     name: 'Trillium Health Partners',           invoiceCount: 8,  totalBilled: '$55,760.00', latestPeriod: 'Apr 1 – Apr 15' },
    { id: 'brampton',     name: 'Brampton Civic Hospital',            invoiceCount: 5,  totalBilled: '$34,920.00', latestPeriod: 'Mar 16 – Mar 31' },
    { id: 'markham',      name: 'Markham Stouffville Hospital',       invoiceCount: 4,  totalBilled: '$27,440.00', latestPeriod: 'Mar 1 – Mar 15' },
    { id: 'london',       name: 'London Health Sciences Centre',      invoiceCount: 10, totalBilled: '$70,180.00', latestPeriod: 'Apr 1 – Apr 15' },
    { id: 'mountsinai',   name: 'Mount Sinai Hospital',               invoiceCount: 7,  totalBilled: '$48,650.00', latestPeriod: 'Apr 1 – Apr 15' },
    { id: 'juravinski',   name: 'Juravinski Cancer Centre',           invoiceCount: 3,  totalBilled: '$19,230.00', latestPeriod: 'Mar 16 – Mar 31' },
    { id: 'southlake',    name: 'Southlake Regional Health Centre',   invoiceCount: 6,  totalBilled: '$40,560.00', latestPeriod: 'Apr 1 – Apr 15' },
];

// ── Props ────────────────────────────────────────────────────────────────────

interface WorkyMainDemoOpsClientInvoicesScreenProps {
    readonly isKingstonExpanded: boolean;
}

// ── Component ────────────────────────────────────────────────────────────────

const WorkyMainDemoOpsClientInvoicesScreen = ({
    isKingstonExpanded,
}: WorkyMainDemoOpsClientInvoicesScreenProps): React.ReactElement => (
    <div className='wci-container'>

        {/* ── Toolbar ──────────────────────────────────────────────── */}
        <div className='wci-toolbar'>
            <span className='wci-title'>Client Invoices</span>
            <div className='wci-search'>
                <Search size={10} className='wci-search-icon' />
                Search by client name...
            </div>
            <button className='wci-configure-btn'>
                <Settings size={10} />
                Configure Invoice
            </button>
        </div>

        {/* ── Scrollable body ──────────────────────────────────────── */}
        <div className='wci-content'>

            {/* ── Featured: Kingston Health SC ─────────────────────── */}
            <div className='wci-featured-card'>
                <div className='wci-featured-header'>
                    <div className='wci-featured-icon'>
                        <FileText size={14} />
                    </div>

                    <div className='wci-featured-identity'>
                        <div className='wci-featured-name-row'>
                            <span className='wci-featured-name'>Kingston Health Sciences Centre</span>
                            <span className='wci-new-badge'>New Invoice</span>
                        </div>
                        <span className='wci-featured-count'>8 invoices</span>
                    </div>

                    <div className='wci-featured-meta'>
                        <div className='wci-meta-item'>
                            <span className='wci-meta-label'>Total Billed</span>
                            <span className='wci-meta-value'>$49,397.00</span>
                        </div>
                        <div className='wci-meta-item'>
                            <span className='wci-meta-label'>Latest Invoice</span>
                            <span className='wci-meta-value' style={{ color: '#2563eb' }}>Apr 1 – Apr 15</span>
                        </div>
                    </div>

                    <button
                        className={`wci-expand-btn${isKingstonExpanded ? ' wci-expand-btn--open' : ''}`}
                        data-demo-target='invoice-kingston-expand'
                    >
                        <ChevronRight size={14} />
                    </button>
                </div>

                {/* Accordion invoice rows */}
                {isKingstonExpanded && (
                    <div className='wci-invoice-rows'>
                        <div className='wci-invoice-row-header'>
                            <span className='wci-inv-th'>#</span>
                            <span className='wci-inv-th'>Billing Period</span>
                            <span className='wci-inv-th' style={{ textAlign: 'center' }}>Stubs</span>
                            <span className='wci-inv-th wci-inv-th--right'>Amount</span>
                            <span className='wci-inv-th' />
                        </div>

                        {KINGSTON_INVOICES.map((inv: InvoiceRow) => (
                            <div
                                key={inv.id}
                                className={`wci-invoice-row${inv.isLatest ? ' wci-invoice-row--latest' : ''}`}
                                {...(inv.isLatest ? { 'data-demo-target': 'invoice-kingston-latest' } : {})}
                            >
                                <span className='wci-inv-number'>{inv.number}</span>
                                <span className='wci-inv-period'>{inv.period}</span>
                                <span className='wci-inv-stubs'>{inv.stubs}</span>
                                <span className='wci-inv-amount'>{inv.amount}</span>
                                <div className='wci-inv-view'>
                                    <button className='wci-view-btn'>
                                        View <ChevronRight size={8} />
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* ── Other clients grid ───────────────────────────────── */}
            <div className='wci-grid'>
                {OTHER_CLIENTS.map((client: ClientCard) => (
                    <div key={client.id} className='wci-card'>
                        <div className='wci-card-header'>
                            <div className='wci-card-icon'>
                                <FileText size={11} />
                            </div>
                            <span className='wci-card-name'>{client.name}</span>
                            <ChevronRight size={11} className='wci-card-chevron' />
                        </div>
                        <span className='wci-card-count'>{client.invoiceCount} invoices</span>
                        <div className='wci-card-divider' />
                        <div className='wci-card-stats'>
                            <div className='wci-card-stat-row'>
                                <span className='wci-card-stat-label'>Total Billed</span>
                                <span className='wci-card-stat-value'>{client.totalBilled}</span>
                            </div>
                            <div className='wci-card-stat-row'>
                                <span className='wci-card-stat-label'>Latest Invoice</span>
                                <span className='wci-card-stat-period'>{client.latestPeriod}</span>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

        </div>
    </div>
);

export { WorkyMainDemoOpsClientInvoicesScreen };
export type { WorkyMainDemoOpsClientInvoicesScreenProps };
